import _ from "lodash"
import { useContext, useState, useEffect } from "react"
import { gql, useQuery } from "@apollo/client"

import ParticipantRSI from "../../components/ParticipantRSI"
import ProfilePhoto from "../ProfilePhoto"
import { LoaderSmall } from "../../components/Loaders"
import { UserContext } from "../../context/userContext"

export default function ParticipantProfileBoxSmall() {
  const userCtx = useContext(UserContext)
  const [currentParticipant, setCurrentParticipant] = useState(null)
  const [labels, setLabels] = useState({})
  const [loadingLabels, setLoadingLabels] = useState(false)

  const GET_PARTICIPANT = gql`
    query GetParticipant($participant_id: ID!, $organization_id: ID!) {
      participant(
        participant_id: $participant_id
        organization_id: $organization_id
      ) {
        participant_id
        participant_alias
        participant_membership_id
        participant_name_first
        participant_name_last
        participant_name_preferred
        participant_phone_primary
        participant_email_account
        participant_city_primary
        participant_state_primary
        participant_zip_code_primary
        participant_external_id
        participant_profile_image
        participant_membership_status
        program_list
        participant_last_engagement
        participant_last_activity
        id_participant_last_activity
        organization_id
        staff_full_name
        locations
      }
    }
  `

  // Fetch participant data using Apollo's useQuery
  const { loading: loadingParticipant, refetch } = useQuery(GET_PARTICIPANT, {
    variables: {
      participant_id: userCtx.current_participant_id,
      organization_id: userCtx.session.user.organization_id,
    },
    skip: _.isEmpty(userCtx.current_participant_id),
    fetchPolicy: "no-cache",
    onCompleted: ({ participant }) => {
      const cleanedParticipant = _.cloneDeepWith(participant, (value) => {
        if (typeof value === "string") {
          return _.unescape(value)
        }
        return undefined
      })

      setCurrentParticipant(cleanedParticipant)
    },
  })

  // Load labels based on the idLastActivity
  const idLastActivity = currentParticipant?.id_participant_last_activity

  useEffect(() => {
    // If no idLastActivity, reset labels
    if (!idLastActivity) {
      setLabels({})
      return
    }

    setLoadingLabels(true)
    const labelsRef = userCtx.session.labels

    // Subscribe to labels updates via Firestore's onSnapshot
    const unsubscribe = labelsRef.onSnapshot(
      (docSnapshot) => {
        const labelList = docSnapshot.data()
        const reqLabels = _.reduce(
          [idLastActivity],
          (acc, template_name) => {
            const reqTemplate = _.find(labelList, { template_name })
            if (_.isEmpty(reqTemplate)) return acc
            const disabledTemplate = reqTemplate.template_availability === 0
            if (disabledTemplate) {
              return { ...acc, [template_name]: null }
            }
            return { ...acc, [template_name]: reqTemplate.template_title }
          },
          {}
        )
        setLabels(reqLabels)
        setLoadingLabels(false)
      },
      (err) => {
        console.error(`Error fetching labels: ${err}`)
        setLoadingLabels(false)
      }
    )

    return () => unsubscribe() // Cleanup subscription on unmount
  }, [idLastActivity, userCtx.session.labels])

  // Consolidate loading states
  const isLoading = loadingParticipant || loadingLabels

  if (_.isEmpty(userCtx.current_participant_id)) {
    return <div>No Participant selected.....</div>
  }

  if (isLoading) {
    return <LoaderSmall />
  }

  const handleProgramList = _.join(currentParticipant?.program_list, ", ")

  const handleEmail = () => {
    const participantEmail = currentParticipant?.participant_email_account || ""
    const noShow =
      participantEmail.startsWith("noemail") ||
      participantEmail.startsWith("participant")
    if (noShow) return ""
    return participantEmail
  }

  return (
    <div className="profile-content">
      <div className="profile-section nav">
        <ParticipantRSI />

        <div className="participant-information">
          <div>
            <span>Participant ID:</span> {currentParticipant?.participant_alias}
          </div>
          <div>
            <span>External ID:</span>{" "}
            {currentParticipant?.participant_external_id}
          </div>
          <div>
            <span>Address:</span> {currentParticipant?.participant_city_primary}
            ,{currentParticipant?.participant_state_primary}{" "}
            {currentParticipant?.participant_zip_code_primary}
          </div>
          <div>
            <span>Email: </span>
            {handleEmail()}
          </div>
          <div>
            <span>Phone: </span> {currentParticipant?.participant_phone_primary}
          </div>
          <div>
            <span>Assigned Staff: </span> {currentParticipant?.staff_full_name}
          </div>
          <div>
            <span>Primary Location:</span> {currentParticipant?.locations}
          </div>
          <div>
            <span>Program ID(s):</span> {handleProgramList}
          </div>
          <div>
            <span>Last Activity:</span>{" "}
            {labels[idLastActivity] ||
              currentParticipant?.participant_last_activity}
          </div>
          <div>
            <span>Last Activity Date:</span>{" "}
            {currentParticipant?.participant_last_engagement}
          </div>
        </div>
        <div className="profile-participant">
          <h2>
            {currentParticipant?.participant_name_first}{" "}
            {currentParticipant?.participant_name_last}
          </h2>
          <ProfilePhoto />
        </div>
      </div>
    </div>
  )
}
